import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';

import useMqtt from '../../hooks/mqttHooks';
import { decodeUtf8 } from '../../utils/utils';
import { EnumChatAppType, EnumPushTypeNowShip } from '../../constants/enum';
import {
  CHAT_AMOUNT_PIXEL_SCROLL_DOWN,
  NOW_SHIP_MQTT_TOPIC,
} from '../../constants/constants';
import { usePrevious, useWindowWidth } from '../../hooks/commonHooks';
import {
  blurChatChannelInput,
  changeDraftMessage,
  clearUnreadMessage,
  focusChatChannelInput,
  sendMessage,
} from './actions';
import { checkHasUnreadMessage } from './routines';

export const useReceiveNewMessage = (handleNewMessage) => {
  const { mqttClient: mqtt } = useMqtt();
  const handleReceiveMqttMessage = useCallback(
    (topic, rawMessage) => {
      if (rawMessage.length === 0) {
        return;
      }
      try {
        const message = JSON.parse(decodeUtf8(rawMessage));
        if (topic.startsWith(NOW_SHIP_MQTT_TOPIC)) {
          const { push_type: pushType } = message;
          if (pushType === EnumPushTypeNowShip.CHAT.id) {
            console.info('MQTT', topic, message);
            handleNewMessage(EnumChatAppType.NOW_SHIP.id, message);
          }
        }
      } catch (ex) {
        console.error(ex);
      }
    },
    [handleNewMessage],
  );
  const mapState = useCallback(
    state => ({
      restaurants: state.app.restaurants,
    }),
    [],
  );
  const { restaurants } = useMappedState(mapState);
  useEffect(
    () => {
      const listNowShipSubscribes = restaurants.map(
        restaurant => `${NOW_SHIP_MQTT_TOPIC}${restaurant.restaurant_id}`,
      );

      const listSubscribes = [...listNowShipSubscribes];

      if (listSubscribes.length > 0) {
        mqtt.subscribe(listSubscribes);
      }
      return () => {
        if (listSubscribes.length > 0) {
          mqtt.unsubscribe(listSubscribes);
        }
      };
    },
    [mqtt, restaurants],
  );

  useEffect(
    () => {
      mqtt.on('message', handleReceiveMqttMessage);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
};

export const useSplitChatChannelListOrder = (chatChannelListOrder) => {
  const windowWidth = useWindowWidth();
  const maxVisibleChatChannel = Math.floor((windowWidth - 600) / 290);
  const splitPivot = Math.max(
    chatChannelListOrder.length - maxVisibleChatChannel,
    0,
  );
  const hiddenChatChannel = useMemo(
    () => chatChannelListOrder.slice(0, splitPivot),
    [chatChannelListOrder, splitPivot],
  );
  const visibleChatChannel = useMemo(
    () => chatChannelListOrder.slice(splitPivot),
    [chatChannelListOrder, splitPivot],
  );

  return [hiddenChatChannel, visibleChatChannel];
};

export const useChatChannelData = (chatChannelIds) => {
  const mapState = useCallback(
    state => ({
      ...(chatChannelIds
        ? chatChannelIds.reduce(
          (channelData, channelId) => ({
            ...channelData,
            [channelId]: state.chat.chatChannelData[channelId],
          }),
          {},
        )
        : state.chat.chatChannelData),
    }),
    [chatChannelIds],
  );
  return useMappedState(mapState);
};

export const useHandlerInputChatText = ({
  appType,
  id,
  currentChatTab,
  currentChatTabData,
}) => {
  const dispatch = useDispatch();
  const { hasUnreadMessage, draftMessage } = currentChatTabData;

  const handleClearUnreadMessage = useCallback(
    () => {
      if (hasUnreadMessage) {
        dispatch(clearUnreadMessage(appType, id, currentChatTab));
      }
    },
    [hasUnreadMessage, dispatch, appType, id, currentChatTab],
  );
  const handleChangeDraftMessage = useCallback(
    (value) => {
      dispatch(changeDraftMessage(id, currentChatTab, value));
    },
    [dispatch, id, currentChatTab],
  );
  const handleSendMessage = useCallback(
    message => dispatch(sendMessage(appType, id, currentChatTab, message)),
    [dispatch, appType, id, currentChatTab],
  );
  const handleFocusChatText = useCallback(
    () => {
      dispatch(focusChatChannelInput(id));
    },
    [dispatch, id],
  );
  const handleBlurChatText = useCallback(
    () => {
      dispatch(blurChatChannelInput(id));
    },
    [dispatch, id],
  );
  return {
    draftMessage,
    handleClearUnreadMessage,
    handleChangeDraftMessage,
    handleSendMessage,
    handleFocusChatText,
    handleBlurChatText,
  };
};

export const useScrollMessageList = ({
  isOpenFull,
  currentChatTab,
  currentChatTabData,
}) => {
  const messageListRef = useRef();
  const scrollNewMessageRef = useRef();
  // const scrollToNewMessage = () => {
  //   if (scrollNewMessageRef.current) {
  //     scrollNewMessageRef.current.scrollIntoView({ behavior: 'instant' });
  //   }
  // };
  const scrollBottomRef = useRef();
  const scrollToBottom = (mode = 'smooth') => {
    if (scrollBottomRef.current) {
      scrollBottomRef.current.scrollIntoView({ behavior: mode });
    }
  };
  const prevIsOpenFull = usePrevious(isOpenFull, false);
  const previousChatTab = usePrevious(currentChatTab);
  const previousChatTabData = usePrevious(currentChatTabData);
  const { messages: oldMessages } = previousChatTabData;
  const { messages: newMessages } = currentChatTabData;
  const { length: oldLength } = oldMessages;
  const { length: newLength } = newMessages;
  // scroll to suitable position when chat channel state change from
  // close -> open
  useEffect(
    () => {
      if (
        (isOpenFull && !prevIsOpenFull) ||
        previousChatTab !== currentChatTab
      ) {
        scrollToBottom('instant');
      }
    },
    [isOpenFull, currentChatTab, prevIsOpenFull, previousChatTab],
  );
  useEffect(
    () => {
      if (oldLength === 0 && newLength > 0) {
        scrollToBottom('instant');
      }
    },
    [newLength, newMessages, oldLength],
  );
  // scroll bottom when last message id !== previous last message id
  // or chat tab change
  const scrollToBottomTimeout = useRef();
  useEffect(
    () => {
      if (oldLength > 0 && newLength > 0 && messageListRef.current) {
        const oldLastMessageId =
          oldMessages[oldLength - 1].id ||
          oldMessages[oldLength - 1].clientMessageId;
        const newLastMessageId =
          newMessages[newLength - 1].id ||
          newMessages[newLength - 1].clientMessageId;
        const isReceiveNewMessage = oldLastMessageId !== newLastMessageId;
        const {
          scrollHeight,
          clientHeight,
          scrollTop,
        } = messageListRef.current;
        const isAtBottom =
          scrollHeight - (clientHeight + scrollTop) <
          CHAT_AMOUNT_PIXEL_SCROLL_DOWN;
        if (isReceiveNewMessage && isAtBottom) {
          clearTimeout(scrollToBottomTimeout.current);
          scrollToBottomTimeout.current = setTimeout(scrollToBottom, 50);
        }
      }
      return () => {
        clearTimeout(scrollToBottomTimeout.current);
      };
    },
    [newLength, newMessages, oldLength, oldMessages],
  );

  return {
    messageListRef,
    scrollNewMessageRef,
    scrollBottomRef,
    scrollToBottom,
  };
};

export const useChangeDocumentTitleSeenMessage = () => {
  const mapState = useCallback(
    state => ({
      chat: state.chat,
    }),
    [],
  );
  const { chat } = useMappedState(mapState);
  const prevChat = usePrevious(chat);
  useEffect(
    () => {
      const isOldChatHasNewMessage = checkHasUnreadMessage(prevChat);
      const isNewChatHasNewMessage = checkHasUnreadMessage(chat);
      if (isOldChatHasNewMessage !== isNewChatHasNewMessage) {
        if (isNewChatHasNewMessage) {
          document.title = `* ${document.title.replace('* ', '')}`;
        } else {
          document.title = document.title.replace('* ', '');
        }
      }
    },
    [chat, prevChat],
  );
};

export const useCalculateBottomFooter = () => {
  const FOOTER_HEIGHT = 70;
  const ref = useRef();
  function watchDocumentHeight() {
    const html = document.documentElement;
    const offsetBottom = Math.max(
      html.scrollTop + html.clientHeight + FOOTER_HEIGHT - html.scrollHeight,
      0,
    );
    const offsetBottomStr = `${offsetBottom}px`;
    if (ref.current && offsetBottomStr !== ref.current.style.bottom) {
      ref.current.style.bottom = offsetBottomStr;
    }
    window.requestAnimationFrame(watchDocumentHeight);
  }
  useLayoutEffect(() => {
    window.requestAnimationFrame(watchDocumentHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
};

export const usePreventBubblingScroll = (messageListRef) => {
  const handleScroll = (e) => {
    if (messageListRef.current) {
      const ele = messageListRef.current;
      if (e.nativeEvent.deltaY <= 0) {
        /* scrolling up */
        if (messageListRef.current.scrollTop <= 0) {
          e.preventDefault();
        }
      } else if (ele.scrollTop + ele.clientHeight >= ele.scrollHeight) {
        e.preventDefault();
      }
    }
  };
  return {
    handleScroll,
  };
};



// WEBPACK FOOTER //
// ./foody/src/pages/ChatBox/hooks.js